export const NEW_SUBSCRIBER_HEADER = "X-New-Subscriber";

const CAMPAIGN_KEY = "finq_campaign";
const LAND_KEY = "finq_land_sent";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export type CampaignParams = Partial<Record<(typeof UTM_KEYS)[number], string>>;
export type SubscriberKind = "new" | "returning" | "unknown";

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    gtag?: Gtag;
  }
}

function readStorage(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Private mode / quota must never break signup.
  }
}

function readStoredCampaign(): CampaignParams {
  try {
    const raw = readStorage(CAMPAIGN_KEY);
    if (!raw) {
      return {};
    }
    return JSON.parse(raw) as CampaignParams;
  } catch {
    return {};
  }
}

export function captureCampaignFromUrl(
  search: string = typeof window === "undefined" ? "" : window.location.search
): CampaignParams {
  try {
    const params = new URLSearchParams(search);
    const fromUrl: CampaignParams = {};
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) {
        fromUrl[key] = value;
      }
    }
    if (Object.keys(fromUrl).length > 0) {
      writeStorage(CAMPAIGN_KEY, JSON.stringify(fromUrl));
      return fromUrl;
    }
    return readStoredCampaign();
  } catch {
    return {};
  }
}

export function getCampaign(): CampaignParams {
  return readStoredCampaign();
}

export function track(
  event: string,
  extra: Record<string, string | number | boolean> = {}
): void {
  try {
    const params = {
      ...captureCampaignFromUrl(),
      ...extra,
    };
    if (typeof window.gtag === "function") {
      window.gtag("event", event, params);
    }
  } catch {
    // Analytics must never block OTP send / verify.
  }
}

export function trackLand(): void {
  try {
    captureCampaignFromUrl();
    if (readStorage(LAND_KEY) === "1") {
      return;
    }
    writeStorage(LAND_KEY, "1");
    track("land", { page_path: window.location.pathname || "/" });
  } catch {
    // ignore
  }
}

export function subscriberKindFromResponse(headers: Headers): SubscriberKind {
  try {
    const value = headers.get(NEW_SUBSCRIBER_HEADER);
    if (value === "true") {
      return "new";
    }
    if (value === "false") {
      return "returning";
    }
    return "unknown";
  } catch {
    return "unknown";
  }
}

export function trackOtpVerified(headers: Headers): void {
  track("otp_ok");
  const subscriberKind = subscriberKindFromResponse(headers);
  switch (subscriberKind) {
    case "new":
      track("new_signup");
      return;
    case "returning":
      track("login");
      return;
    case "unknown":
      return;
    default: {
      const _exhaustive: never = subscriberKind;
      void _exhaustive;
    }
  }
}
